import type { Schemas } from "@platform/contracts/client";
import {
  Button,
  caseTone,
  formatMoney,
  Input,
  Label,
  minorAmount,
  PageHeader,
  Panel,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from "@platform/ui";
import { ErrorText, errorKey, localName, useSession } from "@platform/ui/features";
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslations } from "use-intl";
import { ConsoleCrumb } from "../components/crumb";
import { type Assignee, QueueFrame, QueueToolbar } from "../components/queue";
import { formatAge, formatDateTime, locale } from "../lib/i18n";
import { MessageBody } from "./conversation-messages";

type Summary = Schemas["OpsDisputeSummary"];
type Dispute = Schemas["OpsDispute"];
type State = "open" | "decided";
type Outcome = "grant" | "partial" | "reject";

/** Reasons both parties read must say something: the API asks for 20 characters at least. */
const MIN_REASONS = 20;

const money = (amount: Schemas["Money"]) => formatMoney(amount, locale);

/** Whether the evidence window still runs, or the case waits for a decision (ADM-2's SLA state). */
function slaText(t: ReturnType<typeof useTranslations>, evidenceDueAt: string | null, now = Date.now()) {
  return evidenceDueAt !== null && new Date(evidenceDueAt).getTime() > now
    ? t("admin.desk.evidenceOpen", { date: formatDateTime(evidenceDueAt) })
    : t("admin.desk.ready");
}

/**
 * The dispute desk (AFS-3, ADM-2, card 090): escalated cases and appeals, oldest escalation first, with each one's age,
 * resolver, and whether its evidence window still runs, for operators who resolve disputes.
 */
export function DisputesPage() {
  const t = useTranslations();
  const { session, state: auth } = useSession();
  const me = auth.status === "signed_in" ? auth.claims.sub : undefined;
  const [state, setState] = useState<State>("open");
  const [assignee, setAssignee] = useState<Assignee>("any");
  const [rows, setRows] = useState<readonly Summary[] | undefined>();

  useEffect(() => {
    setRows(undefined);
    void session.api
      .GET("/v1/ops/disputes", { params: { query: { state, assignee } } })
      .then(({ data }) => setRows(data?.items ?? []));
  }, [session, state, assignee]);

  const resolver = (row: Summary) =>
    t(
      row.assigneeOperatorId === null
        ? "admin.desk.nobody"
        : row.assigneeOperatorId === me
          ? "admin.desk.you"
          : "admin.desk.someone",
    );

  return (
    <section aria-labelledby="desk-title" className="flex flex-col gap-5">
      <PageHeader id="desk-title" title={t("admin.desk.title")} description={t("admin.desk.intro")} />
      <QueueToolbar
        id="desk"
        states={["open", "decided"] as const}
        state={state}
        onState={setState}
        stateName={(each) => t(`admin.desk.${each}`)}
        assignee={assignee}
        onAssignee={setAssignee}
      />
      <QueueFrame rows={rows} empty={t("admin.desk.none")}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t("admin.desk.order")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.buyer")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.seller")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.request")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.status")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.escalated")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.desk.time")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.queue.assignee")}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link
                    className="font-medium text-ink tabular-nums hover:text-brand-600"
                    to={`/disputes/${row.id}`}
                  >
                    {row.orderNumber}
                  </Link>
                </TableCell>
                <TableCell>{localName(row.buyer.name, locale)}</TableCell>
                <TableCell>{localName(row.seller.name, locale)}</TableCell>
                <TableCell>
                  <span className="tabular-nums">
                    {t(`aftersale.type.${row.type}`)}
                    {row.requested !== null && ` · ${money(row.requested)}`}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge
                      label={t(`aftersale.status.${row.status}`)}
                      tone={caseTone(row.status, null)}
                    />
                    {row.appeal && <StatusBadge label={t("admin.desk.appeal")} tone="attention" />}
                  </span>
                </TableCell>
                <TableCell className="tabular-nums" title={row.escalatedAt ?? undefined}>
                  {row.escalatedAt === null ? "" : formatAge(row.escalatedAt)}
                </TableCell>
                <TableCell>{slaText(t, row.evidenceDueAt)}</TableCell>
                <TableCell className="text-ink-muted">{resolver(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueueFrame>
    </section>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Panel title={title} className="text-small">
      {children}
    </Panel>
  );
}

/**
 * One dispute for its resolver (card 090): what the buyer asks and the seller answered, both sides' evidence with the
 * messages they picked and links to their files, the return and settlement, every decision, and the timeline. The
 * resolver takes the case, then decides it with reasons both parties read; an appeal goes to someone else.
 */
export function DisputePage() {
  const t = useTranslations();
  const { caseId = "" } = useParams();
  const { session, state } = useSession();
  const me = state.status === "signed_in" ? state.claims.sub : undefined;
  const permissions = state.status === "signed_in" ? state.claims.perm : [];
  const [view, setView] = useState<Dispute | undefined>();
  const [outcome, setOutcome] = useState<Outcome>("grant");
  const [amount, setAmount] = useState("");
  const [reasons, setReasons] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/disputes/{caseId}", {
      params: { path: { caseId } },
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setView(data);
  }, [session, caseId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const take = async () => {
    setError(undefined);
    const { data, error: problem } = await session.api.POST("/v1/ops/disputes/{caseId}/assign", {
      params: { path: { caseId } },
      body: {},
    });
    if (data === undefined) return setError(t(errorKey(problem)));
    setView(data);
  };

  const decide = async (event: FormEvent) => {
    event.preventDefault();
    if (view === undefined) return;
    if (reasons.trim().length < MIN_REASONS) return setError(t("admin.desk.tooShort"));
    const amountMinor = outcome === "partial" ? minorAmount(amount, view.paid.currency) : undefined;
    if (outcome === "partial" && amountMinor === undefined)
      return setError(t("admin.invalid", { field: t("admin.desk.amount") }));
    setBusy(true);
    setError(undefined);
    try {
      const { data, error: problem } = await session.api.POST("/v1/ops/disputes/{caseId}/decide", {
        params: { path: { caseId } },
        body: {
          outcome,
          reasons: reasons.trim(),
          ...(amountMinor === undefined ? {} : { amount: { amountMinor, currency: view.paid.currency } }),
        },
      });
      if (data === undefined) return setError(t(errorKey(problem)));
      setView(data);
    } finally {
      setBusy(false);
    }
  };

  if (view === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const buyer = localName(view.buyer.name, locale);
  const seller = localName(view.seller.name, locale);
  const sideName = (side: "buyer" | "seller") => (side === "buyer" ? buyer : seller);
  const fileOf = new Map(view.files.map((file) => [file.fileId, file]));
  const appealing = view.appeal?.status === "filed";
  const hearsAppeals = permissions.includes("ops:appeal.resolve") && view.firstResolverOperatorId !== me;
  const mayTake =
    (view.status === "escalated" || view.status === "under_review") &&
    view.assigneeOperatorId !== me &&
    (!appealing || hearsAppeals);
  const decidesNow = view.status === "under_review" && view.assigneeOperatorId === me;
  // A first decision waits for the parties' evidence period (platform terms 9.3, card 128).
  const evidenceOpen =
    !appealing && view.evidenceDueAt !== null && new Date(view.evidenceDueAt).getTime() > Date.now();
  const latest = view.decisions.at(-1);
  const readsContent = permissions.includes("ops:support.content-access");

  return (
    <section aria-labelledby="dispute-title" className="flex flex-col gap-5">
      <PageHeader
        id="dispute-title"
        breadcrumb={<ConsoleCrumb to="/disputes" back={t("admin.desk.title")} current={view.orderNumber} />}
        title={t("admin.desk.caseTitle", { number: view.orderNumber })}
        marks={
          <>
            <StatusBadge label={t(`aftersale.status.${view.status}`)} tone={caseTone(view.status, null)} />
            {appealing && <StatusBadge label={t("admin.desk.appeal")} tone="attention" />}
          </>
        }
        description={`${t(`aftersale.type.${view.type}`)} · ${t("admin.desk.assignee", {
          who: t(
            view.assigneeOperatorId === null
              ? "admin.desk.nobody"
              : view.assigneeOperatorId === me
                ? "admin.desk.you"
                : "admin.desk.someone",
          ),
        })}`}
        actions={
          <>
            <Link
              to={`/orders/${view.orderId}`}
              className="text-small font-medium text-brand-600 hover:text-brand-700"
            >
              {t("admin.desk.openOrder")}
            </Link>
            {view.conversationId !== null && readsContent && (
              <Link
                to={`/conversations/${view.conversationId}/messages?orderId=${view.orderId}`}
                className="text-small font-medium text-brand-600 hover:text-brand-700"
              >
                {t("admin.desk.readMessages")}
              </Link>
            )}
          </>
        }
      />

      <Section title={t("admin.desk.asks")}>
        <p>{`${t("admin.desk.buyer")}: ${buyer} · ${t("admin.desk.seller")}: ${seller}`}</p>
        <p>
          {view.requested === null
            ? t("admin.desk.replacement")
            : t("admin.desk.requested", { amount: money(view.requested) })}
        </p>
        <p>{t("admin.desk.paid", { amount: money(view.paid) })}</p>
        {view.settlement === "between_parties" && (
          <p className="text-ink-muted">{t("admin.desk.settledBetween")}</p>
        )}
        {view.lines.length > 0 && <p>{t("admin.desk.lines", { count: view.lines.length })}</p>}
        <p className="text-ink-muted">{t("admin.desk.reason")}</p>
        <p className="whitespace-pre-wrap">{view.reason}</p>
      </Section>

      <Section title={t("admin.desk.answer")}>
        {view.answer === null ? (
          <p>{t("admin.desk.noAnswer")}</p>
        ) : (
          <>
            <p>{t(`aftersale.answer.${view.answer}`)}</p>
            {view.counter !== null && <p>{t("admin.desk.counter", { amount: money(view.counter) })}</p>}
            {view.answerNote !== null && <p className="whitespace-pre-wrap">{view.answerNote}</p>}
          </>
        )}
      </Section>

      <Section title={t("admin.desk.evidence")}>
        {view.evidence.length === 0 && <p className="text-ink-muted">{t("admin.desk.noEvidence")}</p>}
        <ul className="flex flex-col gap-3">
          {view.evidence.map((item) => {
            const file = item.fileId === null ? undefined : fileOf.get(item.fileId);
            return (
              <li key={item.id} className="flex flex-col gap-1 border-t border-border-subtle pt-3">
                <span className="text-caption text-ink-muted">
                  {`${t(`aftersale.side.${item.side}`)} · ${sideName(item.side)} · ${formatDateTime(item.at)}`}
                  {item.late && ` · ${t("admin.desk.late")}`}
                </span>
                {item.kind === "file" && (
                  <a
                    href={file?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="self-start text-brand-600 hover:text-brand-700"
                  >
                    {file?.name ?? t("admin.desk.file")}
                  </a>
                )}
                {item.kind === "note" && <p className="whitespace-pre-wrap">{item.note}</p>}
                {item.kind === "conversation_export" && (
                  <div className="flex flex-col gap-1">
                    <p>{t("admin.desk.messages", { count: item.messages?.length ?? 0 })}</p>
                    <ol className="flex flex-col gap-1 rounded-(--radius-control) bg-subtle p-3">
                      {(item.messages ?? []).map((message) => (
                        <li key={message.id}>
                          <span className="text-caption text-ink-muted">
                            {`${message.sender.displayName} · ${formatDateTime(message.sentAt)}`}
                          </span>
                          <MessageBody message={message} />
                        </li>
                      ))}
                    </ol>
                    <p className="break-all font-mono text-caption text-ink-muted">
                      {t("admin.desk.checksum", { sha: item.sha256 ?? "" })}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Section>

      {(view.returnShipment !== null || view.settlementRecord !== null) && (
        <Section title={t("admin.desk.settlement")}>
          {view.returnShipment !== null && (
            <>
              <p>
                {t("admin.desk.returnSent", {
                  carrier: view.returnShipment.carrier,
                  tracking: view.returnShipment.trackingNumber,
                })}
              </p>
              {view.returnShipment.receivedAt !== null && (
                <p>
                  {t("admin.desk.returnReceived", { date: formatDateTime(view.returnShipment.receivedAt) })}
                </p>
              )}
            </>
          )}
          {view.settlementRecord !== null && (
            <p>
              {view.settlementRecord.kind === "refund" && view.settlementRecord.amount !== null
                ? t("admin.desk.refundRecorded", {
                    amount: money(view.settlementRecord.amount),
                    date: view.settlementRecord.paidOn ?? "",
                    reference: view.settlementRecord.reference ?? "",
                  })
                : t("admin.desk.replacementRecorded", {
                    carrier: view.settlementRecord.carrier ?? "",
                    tracking: view.settlementRecord.trackingNumber ?? "",
                  })}
            </p>
          )}
        </Section>
      )}

      {(view.decisions.length > 0 || view.appeal !== null) && (
        <Section title={t("admin.desk.decisions")}>
          {view.decisions.map((decision) => (
            <div key={decision.decidedAt} className="flex flex-col gap-1 border-t border-border-subtle pt-3">
              <p className="font-medium">
                {t(`aftersale.outcome.${decision.outcome}`)}
                {decision.amount !== null && ` · ${money(decision.amount)}`}
                {` · ${formatDateTime(decision.decidedAt)}`}
                {decision.appeal && ` · ${t("admin.desk.onAppeal")}`}
              </p>
              <p className="whitespace-pre-wrap">{decision.reasons}</p>
            </div>
          ))}
          {view.appeal !== null && (
            <p>
              {t("admin.desk.appealFiled", {
                side: t(`aftersale.side.${view.appeal.side}`),
                date: formatDateTime(view.appeal.filedAt),
                reason: view.appeal.reason,
              })}
            </p>
          )}
          {view.status === "resolved" && latest !== undefined && (
            <p className="text-ink-muted">
              {latest.appeal || view.appealDueAt === null
                ? t("admin.desk.final")
                : t("admin.desk.appealWindow", { date: formatDateTime(view.appealDueAt) })}
            </p>
          )}
        </Section>
      )}

      {appealing && !hearsAppeals && (
        <p className="text-small text-ink-muted">{t("admin.desk.appealElsewhere")}</p>
      )}
      {mayTake && (
        <Button type="button" variant="secondary" className="self-start" onClick={() => void take()}>
          {t("admin.desk.take")}
        </Button>
      )}
      {view.status === "under_review" &&
        view.assigneeOperatorId !== null &&
        view.assigneeOperatorId !== me && (
          <p className="text-small text-ink-muted">{t("admin.desk.notYours")}</p>
        )}
      {decidesNow && (
        <Panel title={t("admin.desk.decide")} highlight>
          <form className="flex flex-col gap-3" onSubmit={(event) => void decide(event)}>
            <fieldset className="flex flex-wrap gap-2 text-small">
              {(["grant", "partial", "reject"] as const)
                .filter((option) => option !== "partial" || view.type !== "replacement")
                .map((option) => (
                  <label
                    key={option}
                    className="flex cursor-pointer items-center gap-2 rounded-(--radius-control) border border-border px-3 py-2 transition-colors duration-(--duration-fast) hover:bg-subtle has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50"
                  >
                    <input
                      type="radio"
                      className="size-4 accent-brand-600"
                      name="outcome"
                      value={option}
                      checked={outcome === option}
                      onChange={() => setOutcome(option)}
                    />
                    {t(`aftersale.outcome.${option}`)}
                  </label>
                ))}
            </fieldset>
            {outcome === "partial" && (
              <div className="flex max-w-xs flex-col gap-1.5">
                <Label htmlFor="decision-amount">{`${t("admin.desk.amount")} (${view.paid.currency})`}</Label>
                <Input
                  id="decision-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="decision-reasons">{t("admin.desk.reasons")}</Label>
              <Textarea
                id="decision-reasons"
                rows={5}
                value={reasons}
                onChange={(event) => setReasons(event.target.value)}
              />
            </div>
            {error !== undefined && <ErrorText>{error}</ErrorText>}
            {evidenceOpen && view.evidenceDueAt !== null && (
              <p className="text-small text-ink-muted">
                {t("admin.desk.decideAfterEvidence", { date: formatDateTime(view.evidenceDueAt) })}
              </p>
            )}
            <Button type="submit" className="self-start" disabled={busy || evidenceOpen}>
              {t("admin.desk.record")}
            </Button>
          </form>
        </Panel>
      )}
      {!decidesNow && error !== undefined && <ErrorText>{error}</ErrorText>}

      <Section title={t("admin.desk.timeline")}>
        <ol className="flex flex-col gap-1">
          {view.timeline.map((entry) => (
            <li key={`${entry.at}-${entry.action}`}>
              <span className="text-ink-muted">{`${formatDateTime(entry.at)} · ${t(`aftersale.side.${entry.actor}`)} · `}</span>
              {t(`aftersale.action.${entry.action}`)}
            </li>
          ))}
        </ol>
      </Section>
    </section>
  );
}
